import { useEffect } from 'react';
import { useTranslation } from 'next-i18next';

import { useFeedback } from '@phoenix/core/frontend/shared/ui/feedback';
import { useBreakpoints } from '@phoenix/design-system/frontend/ui/breakpoints/use-breakpoints';

import { useUserSettings } from './use-user-settings';
import { useUserPreferencesContext } from './use-user-preferences-context';

type FeedbackSetting = {
  visited: boolean;
  dateShown: string | null;
};

export function useDashboardFeedback({ supportLink, eventName = 'mystihl_user_feedback' }: { supportLink: string; eventName?: string }) {
  const {
    setActive,
    showFeedbackPanel,
    setFeedbackLabels,
    setQuestion,
    isFeedbackPanelOpen,
    setTrackingContext,
    setShowFeedbackBanner,
    setShowFeedbackFab,
    setSecondaryButton,
    hideFeedbackPanel,
    setSupportHint,
    setEventName,
  } = useFeedback();

  const { saveUserPreferences, feedbackPanelAlwaysCollapsed, isLoading: isLoadingPreferences } = useUserPreferencesContext();

  const { t } = useTranslation();
  const { t: tShared } = useTranslation('shared');
  const { desktop } = useBreakpoints();

  const [data, mutateSettingService] = useUserSettings('dashboard.feedback');
  const mutateSetting = (dateShown?: Date) => mutateSettingService({ visited: true, dateShown: dateShown?.toISOString() });

  useEffect(() => {
    if (data) {
      const { dateShown, visited } = (data.userSetting?.setting || {}) as FeedbackSetting;

      if (!visited) {
        mutateSetting();
      } else if (!isLoadingPreferences && !feedbackPanelAlwaysCollapsed && !dateShown) {
        showFeedbackPanel();
      }
    }
  }, [data]);

  useEffect(() => {
    if (isFeedbackPanelOpen) {
      mutateSetting(new Date());
    }
  }, [isFeedbackPanelOpen, data, isLoadingPreferences]);

  const onClickSecondaryButton = () => {
    saveUserPreferences({
      feedbackPanelAlwaysCollapsed: true,
    });
    hideFeedbackPanel();
  };

  useEffect(() => {
    setQuestion(t('userFeedback_dashboard_recommendation'));
    setFeedbackLabels(
      tShared('userFeedback_scaleRecommendation_label_negative'),
      tShared('userFeedback_scaleRecommendation_label_positive')
    );
    setSupportHint(tShared('userFeedback_consumer_support_copy'), supportLink);
    setActive(true);
    setTrackingContext('dashboard');
    feedbackPanelAlwaysCollapsed
      ? setSecondaryButton(undefined)
      : setSecondaryButton({
          text: tShared('userFeedback_button_secondary'),
          onClick: onClickSecondaryButton,
        });

    return () => setActive(false);
  }, [feedbackPanelAlwaysCollapsed, isFeedbackPanelOpen, supportLink]);

  useEffect(() => {
    setShowFeedbackBanner(desktop);
    setShowFeedbackFab(!desktop);
  }, [desktop]);

  useEffect(() => {
    setEventName?.(eventName);
  }, []);
}
